import React, { Fragment, useEffect, useState, useRef } from 'react';
import { Table, DatePicker, message, Input, Button, Select } from 'antd';
import { getClearanceTimeImportReportService } from '../../../services/ClearanceReportService';
import { getAllAdminIECsService } from '../../../services/FileDataService';
import { getBRCorelationService } from '../../../services/MasterService';
import Card from '../../../components/bootstrap/card';
import { Col, Row } from 'react-bootstrap';
import { Excel } from 'antd-table-saveas-excel';
import moment from 'moment';
import { getTodaysDate, formatToTwoDecimals } from '../../../utilities/utils';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { filterHash } from '@fullcalendar/core/internal';
import { useLocation } from 'react-router-dom';
import ReportDateFilter from '../../../components/ReportDateFilter';
import useColumnVisibility from '../../../hooks/useColumnVisibility';
import ColumnSelector from '../../../components/common/ColumnSelector';

const { RangePicker } = DatePicker;

const ClearanceTimeImportReport = () => {
  const location = useLocation();
  const [data, setData] = useState([]);
  const [IECOptions, setIECOptions] = useState([]);
  const userRole = localStorage.getItem('role');
  const [selectedIEC, setSelectedIEC] = useState(
    localStorage.getItem('iec_no')
  );
  const tableRef = useRef(null);
  const [selectedIECName, setSelectedIECName] = useState(localStorage.getItem('name'))
  const [isValid, setIsValid] = useState(true); // Validation state
  const [billNo, setBillNo] = useState("");
  const [branchCodeNames, setBranchCodeNames] = useState([]);
  const [selectedBranchCodes, setSelectedBranchCodes] = useState([]);

  const tableStyles = `
    .clearance-import-table .ant-table-thead > tr > th {
      white-space: normal !important;
      word-break: break-word !important;
      overflow: visible !important;
      text-overflow: unset !important;
      vertical-align: middle !important;
      line-height: 1.3 !important;
      padding-top: 8px !important;
      padding-bottom: 8px !important;
    }
    .clearance-import-table .ant-table-tbody > tr > td {
      white-space: nowrap;
    }
    .clearance-import-table .ant-table-container {
      overflow-x: auto !important;
    }
    .clearance-import-table .ant-table-header {
      overflow: hidden !important;
    }
    .clearance-import-table .ant-table-sticky-holder {
      overflow: hidden !important;
    }
  `;

  const currentYear = dayjs().year();
  const defaultFrom = dayjs(`${currentYear}-01-01`);
  const defaultTo = dayjs(`${currentYear}-12-31`);
  const [dates, setDates] = useState(() => {
    // if report is opened through navigation from dashboard, then use those dates else default
    if (location.state?.passedDates?.length === 2) {
      return [
        dayjs(location.state.passedDates[0]),
        dayjs(location.state.passedDates[1]),
      ];
    }
    return [defaultFrom, defaultTo];
  });

  useEffect(() => {
    if (userRole === 'superadmin' || userRole === 'group_head') {
      getAdminIEC();
    }
    if (userRole === 'admin' || userRole === 'user') {
      fetchData();
    }
    if (selectedIEC) {
      getBRCorelationService(selectedIEC).then((response) => {
        setBranchCodeNames(response);
      });
    }
  }, []);

  useEffect(() => {
    if (selectedIEC) {
      getBRCorelationService(selectedIEC).then((response) => {
        setBranchCodeNames(response);
      });
    }
  }, [selectedIEC]);

  const getDataToRender = () => {
    if (tableRef.current) {
      const tableData = data;
      return tableData;
    }
    return [];
  };

  const fetchData = async () => {
    const filters = {
      from_date: dates?.[0] ? dayjs(dates[0]).format("YYYY-MM-DD") : null,
      to_date: dates?.[1] ? dayjs(dates[1]).format("YYYY-MM-DD") : null,
      bill_no: billNo || null,
      br_code: selectedBranchCodes || null,
    };
    try {
      // Call the service function to fetch the clearance time import report data
      const response = await getClearanceTimeImportReportService(selectedIEC, filters); // Define this service function
      const processedData = preprocessData(response);
      setData(processedData);
    } catch (error) {
      console.error('Error fetching clearance time import report data:', error);
    }
  };

  function formatDateToDDMMYY(dateString) {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return ''; // Return empty string if the dateString is not a valid date
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  }

  const preprocessData = (data) => {
    return data.map(record => {

      record.daysIgmSub = calculateDaysDifference(record.submission_date, record.igm_date);
      record.daysSubToOoc = calculateDaysDifference(record.ooc_date, record.submission_date);
      record.daysIgmToOoc = calculateDaysDifference(record.ooc_date, record.igm_date);
      return record;
    });
  };

  const calculateDaysDifference = (date1, date2) => {

    if (date1 && date2) {
      const diffTime = Math.abs(new Date(date2) - new Date(date1));
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return diffDays;
    }
    else {
      return ''
    }
  };

  const columns = [
    {
      title: 'BUSINESS UNIT',
      dataIndex: 'business_unit',
      key: 'business_unit',
      header: 'BUSINESS UNIT',
      width: 160, fixed: 'left'
    },
    {
      title: 'PLANT NAME',
      dataIndex: 'plant_name',
      key: 'plant_name',
      header: 'PLANT NAME',
      width: 150, fixed: 'left'
    },
    {
      title: 'IEC/BR CODE',
      dataIndex: 'boe_branch_code',
      key: 'boe_branch_code',
      header: 'IEC/BR CODE',
      width: 140,
    },
    {
      title: 'CB CODE',
      dataIndex: 'boe_cb_code',
      key: 'boe_cb_code',
      header: 'CB CODE',
      width: 110,
    },
    {
      title: 'CB NAME',
      dataIndex: 'cb_name',
      key: 'cb_name',
      header: 'CB NAME',
      width: 110,
    },
    {
      title: 'PORT',
      dataIndex: 'port_code',
      key: 'port_code',
      header: 'PORT',
      width: 100,
    },
    {
      title: 'BOE NO.',
      dataIndex: 'be_no',
      key: 'be_no',
      header: 'BOE NO.',
      width: 115,
    },
    {
      title: 'BOE DT.',
      dataIndex: 'boe_date',
      key: 'boe_date',
      header: 'BOE DT.',
      width: 110,
    },
    {
      title: 'IGM DT.',
      dataIndex: 'igm_date',
      key: 'igm_date',
      header: 'IGM DT.',
      width: 110,
    },
    {
      title: 'SUBMISSION DT.',
      dataIndex: 'submission_date',
      key: 'submission_date',
      header: 'SUBMISSION DT.',
      width: 160,
    },
    {
      title: 'ASSESSMENT DT.',
      dataIndex: 'assessment_date',
      key: 'assessment_date',
      header: 'ASSESSMENT DT.',
      width: 160,
    },
    {
      title: 'EXAMINATION DT.',
      dataIndex: 'examination_date',
      key: 'examination_date',
      header: 'EXAMINATION DT.',
      width: 170,
    },
    {
      title: 'OOC DT.',
      dataIndex: 'ooc_date',
      key: 'ooc_date',
      header: 'OOC DT.',
      width: 110,
    },
    {
      title: 'INTEREST PAID - INR',
      dataIndex: 'int',
      key: 'int',
      header: 'INTEREST PAID - INR',
      align: 'right',
      width: 210,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'PENLTY PAID - INR',
      dataIndex: 'pnlty',
      key: 'pnlty',
      header: 'PENLTY PAID - INR',
      align: 'right',
      width: 190,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'FINE PAID - INR',
      dataIndex: 'fine',
      key: 'fine',
      header: 'FINE PAID - INR',
      align: 'right',
      width: 170,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'DAYS SUB. TO IGM.',
      dataIndex: 'daysIgmSub',
      key: 'daysIgmSub',
      header: 'DAYS SUB. TO IGM.',
      align: 'right',
      width: 190,
    },
    {
      title: 'DAYS SUB TO OOC',
      dataIndex: 'daysSubToOoc',
      key: 'daysSubToOoc',
      header: 'DAYS SUB TO OOC',
      align: 'right',
      width: 170,
    },
    {
      title: 'DAYS IGM. TO OOC',
      dataIndex: 'daysIgmToOoc',
      key: 'daysIgmToOoc',
      header: 'DAYS IGM. TO OOC',
      align: 'right',
      width: 180,
    },
    { title: 'INW Dt.', dataIndex: 'inw_date', key: 'inw_date', header: 'INW Dt.', width: 110 },
    { title: 'DAYS INW TO OOC', dataIndex: 'daysInwToOoc', key: 'daysInwToOoc', header: 'DAYS INW TO OOC', align: 'right', width: 170 },
    { title: 'MODE', dataIndex: 'mode', key: 'mode', header: 'MODE', width: 90 },
    { title: 'TAT', dataIndex: 'tat', key: 'tat', header: 'TAT', width: 80 },
    { title: 'REMARK', dataIndex: 'remark', key: 'remark', header: 'REMARK', width: 120 },
  ];

  const { visibleKeys, setVisibleKeys, filteredColumns, resetToDefault } =
    useColumnVisibility('ClearanceTimeImport', columns);


  const getDateOnly = (dateString) => {
    if (dateString && dateString.length !== 0) {
      const date = new Date(dateString);
      return date;
    }
    else {
      return '';
    }
  }
  const handleDateChange = (value) => {
    setDates(value || []);
    if (value && value.length === 2) {
      const from_date = value[0];
      const to_date = value[1];

      // Validate that both dates are selected
      if (!from_date || !to_date) {
        setIsValid(false);
        return;
      }

      // Validate that the duration is within 1 year
      const oneYearLater = from_date.add(1, 'year');
      if (to_date.isAfter(oneYearLater)) {
        message.error("The selected duration should not exceed 1 year.");
        setIsValid(false);
        return;
      }

      setIsValid(true);
    } else {
      setIsValid(false);
    }
  };

  const handleSubmit = () => {
    if (!selectedIEC) {
      message.error("Please select an IEC");
      return;
    }
    if (dates.length === 2 && isValid) {
      fetchData();
    }
    else {
      message.error("Please select a valid date range.");
    }
  };

  const getAdminIEC = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await getAllAdminIECsService(token);

      if (response?.data) {
        const options = response.data.map((item, index) => ({
          value: `${item.iec_no};${item.fullname}`,
          label: `${item.iec_no} (${item.fullname})`,
          key: index,
        }));
        setIECOptions(options);
      }
    } catch (err) {
      message.error("Failed to load IEC list");
    }
  };

  const handleIECSelection = (val) => {
    if (val) {
      const [iec_no, iec_name] = val.split(";");
      setSelectedIEC(iec_no);
      setSelectedIECName(iec_name);
    } else {
      setSelectedIEC(null);
      setSelectedIECName('');
    }
  };

  const handleBillSearch = (value) => {
    setBillNo(value);
  };

  const handleBranchCodeSelection = (values) => {
    setSelectedBranchCodes(values);
  };

  const exportToExcel = async () => {
    let workbook = new ExcelJS.Workbook();
    let worksheet = workbook.addWorksheet('Data');


    worksheet.columns = filteredColumns.map(col => ({
      header: col.header || col.title,
      key: col.dataIndex,
      width: 20
    }));
    // Apply styles to the header row (Row 1)
    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true }; // Bold text
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFC000' }, // Yellow background
      };
      cell.alignment = { vertical: 'middle' }; // Center align
    });

    data.forEach((obj) => {
      worksheet.addRow({
        ...obj,
        submission_date: getDateOnly(obj.submission_date),
        boe_date: getDateOnly(obj.boe_date),
        igm_date: getDateOnly(obj.igm_date),
        assessment_date: getDateOnly(obj.assessment_date),
        examination_date: getDateOnly(obj.examination_date),
        ooc_date: getDateOnly(obj.ooc_date),
        int: obj.int ? parseFloat(obj.int) : '',
        pnlty: obj.pnlty ? parseFloat(obj.pnlty) : '',
        fine: obj.fine ? parseFloat(obj.fine) : '',
      })

    });

    // Generate Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    // Use FileSaver to save the file
    saveAs(blob, `Lucrative's CIP DSS - Clearance Time Import as on ${getTodaysDate()} of ${selectedIEC}.xlsx`);
  };


  return (
    <Fragment>
      <style>{tableStyles}</style>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <div className="header-title">
                <h4 className="card-title">Lucrative's CIP TAT -  Import Clearance as on {moment().format('DD/MM/YYYY')} of {selectedIECName}</h4>
              </div>

              <div className="d-flex align-items-center gap-2">
                <ColumnSelector
                  allColumns={columns}
                  visibleKeys={visibleKeys}
                  setVisibleKeys={setVisibleKeys}
                  onReset={resetToDefault}
                />
                <button className="btn btn-primary cip-btn-submit" onClick={exportToExcel}>Export to Excel</button>
              </div>
            </Card.Header>
            <Card.Body className="d-flex justify-content-start align-items-center gap-2 ps-4">
              {(userRole === 'superadmin' || userRole === 'group_head') && (
                <Select
                  showSearch
                  placeholder="Select IEC"
                  onChange={handleIECSelection}
                  allowClear
                  options={IECOptions}
                  style={{ width: '300px' }}
                />
              )}
              <ReportDateFilter dates={dates} onChange={handleDateChange} />
              <Input
                placeholder="Enter Bill No"
                allowClear
                value={billNo}
                onChange={(e) => handleBillSearch(e.target.value)}
                style={{ width: 250 }}
              />
              <Select
                mode="multiple"
                allowClear
                style={{ width: 250 }}
                placeholder="Select Branch Code"
                onChange={handleBranchCodeSelection}
                options={branchCodeNames.map((item) => ({
                  value: item.branch_code,
                  label: `${item.branch_code} / ${item.plant_name} (${item.business_unit})`,
                }))}
              />
              <Button type="primary" className="cip-btn-submit"
                onClick={handleSubmit}>
                Submit
              </Button>
            </Card.Body>
            <Card.Body className="px-0 report-page">
              <Table
                className="clearance-import-table"
                dataSource={data}
                columns={filteredColumns}
                ref={tableRef}
                pagination={{ showTotal: (total) => `Total ${total} records`, showSizeChanger: true }}
                scroll={{ x: 'max-content' }}

              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>

  );
};

export default ClearanceTimeImportReport;
